import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  TbCash, TbRefresh, TbCheck, TbX, TbPlus, TbTrash,
  TbAlertCircle, TbCalendar, TbCurrencyEuro, TbUser,
  TbReceipt, TbCircleCheck, TbHistory, TbFileInvoice,
  TbBuilding, TbChevronDown, TbChevronUp, TbDoorEnter, TbDoorExit
} from "react-icons/tb";

const API = "https://api.komorotel.com/api/agency";
const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => Math.round((eur || 0) * KMF_PER_EUR).toLocaleString("fr-FR");
const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
const fmtShort = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

// Premier et dernier jour du mois courant en YYYY-MM-DD
const monthBounds = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last  = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const iso = (d) => d.toISOString().split("T")[0];
  return { from: iso(first), to: iso(last) };
};

// ─────────────────────────────────────────
// FORMULAIRE AJOUT DÉPENSE
// ─────────────────────────────────────────
function ExpenseForm({ ownerId, hotels, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    label: "", amount: "", date: new Date().toISOString().split("T")[0], hotelId: ""
  });
  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex flex-col gap-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nouvelle dépense</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Libellé *</label>
          <input
            name="label" value={form.label} onChange={handleChange}
            placeholder="Ex : Ménage, Réparation plomberie..."
            className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Montant (€) *</label>
          <input
            type="number" name="amount" min="0" step="0.01" value={form.amount} onChange={handleChange}
            placeholder="0.00"
            className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Date *</label>
          <input
            type="date" name="date" value={form.date} onChange={handleChange}
            className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
          />
        </div>
        <div className="col-span-2 flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Bien concerné (optionnel)</label>
          <select
            name="hotelId" value={form.hotelId} onChange={handleChange}
            className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition"
          >
            <option value="">— Aucun bien spécifique —</option>
            {hotels.map(h => <option key={h._id} value={h._id}>{h.name}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-100 transition">
          Annuler
        </button>
        <button
          onClick={() => onSave({ ...form, ownerId, hotelId: form.hotelId || null })}
          disabled={loading || !form.label.trim() || !form.amount}
          className="px-4 py-2 text-sm bg-[var(--k-green)] text-white rounded-xl hover:bg-[var(--k-green-hover)] transition disabled:opacity-60 flex items-center gap-1.5"
        >
          <TbCheck size={15} /> {loading ? "Ajout..." : "Ajouter"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// LIGNE DE CALCUL
// ─────────────────────────────────────────
function CalcRow({ label, value, color = "text-gray-900", bold = false, negative = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <p className={`text-xs ${bold ? "font-semibold text-gray-700" : "text-gray-500"}`}>{label}</p>
      <p className={`text-sm font-semibold ${color}`}>
        {negative && value > 0 ? "− " : ""}{eur(Math.abs(value))}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────
// HISTORIQUE
// ─────────────────────────────────────────
function PayoutHistoryCard({ payout }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition" onClick={() => setOpen(o => !o)}>
        <div className="w-10 h-10 rounded-full bg-[var(--k-green-soft)] text-[var(--k-green-text)] flex items-center justify-center text-sm font-medium shrink-0">
          {payout.ownerId?.name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900 truncate">{payout.ownerId?.name || "—"}</p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
              <TbCircleCheck size={12} /> Payé
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {fmtShort(payout.period.from)} → {fmtDate(payout.period.to)} · payé le {fmtDate(payout.paidAt)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-gray-900">{eur(payout.netAmount)}</p>
          <p className="text-xs text-gray-400">{toKMF(payout.netAmount)} KMF</p>
        </div>
        {open ? <TbChevronUp size={15} className="text-gray-400 shrink-0" /> : <TbChevronDown size={15} className="text-gray-400 shrink-0" />}
      </div>
      {open && (
        <div className="px-4 pb-4 border-t border-gray-50 pt-3">
          <div className="bg-gray-50 rounded-xl p-4">
            <CalcRow label="Revenus bruts (voyageurs)" value={payout.grossRevenue} />
            {payout.komorotelFee > 0 && <CalcRow label="Commission Komorotel (8%)" value={payout.komorotelFee} color="text-blue-500" negative />}
            <CalcRow label="Commission agence" value={payout.agencyCommission} color="text-orange-500" negative />
            {payout.expenses > 0 && <CalcRow label="Dépenses déduites" value={payout.expenses} color="text-red-400" negative />}
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-700">Net reversé</p>
              <p className="text-base font-bold text-[var(--k-green)]">{eur(payout.netAmount)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">Réservations</p>
              <p className="text-xs font-medium text-gray-800">{payout.reservationIds?.length || 0} incluses</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 mb-0.5">Dépenses</p>
              <p className="text-xs font-medium text-gray-800">{payout.expenseIds?.length || 0} incluses</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// PAGE PRINCIPALE
// ─────────────────────────────────────────
export default function AgencyPayouts() {
  const [tab, setTab] = useState("statement"); // statement | history
  const [owners, setOwners] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [payouts, setPayouts] = useState([]);

  const [selectedOwner, setSelectedOwner] = useState("");
  const [period, setPeriod] = useState(monthBounds());

  const [statement, setStatement] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [error, setError] = useState(null);

  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  // Chargement initial owners + hotels + historique
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/owners`, { withCredentials: true }),
      axios.get(`${API}`, { withCredentials: true }),
      axios.get(`${API}/payouts`, { withCredentials: true }),
    ]).then(([o, h, p]) => {
      setOwners(o.data);
      setHotels(h.data);
      setPayouts(p.data);
    }).catch(() => setError("Erreur de chargement."));
  }, []);

  // Biens du propriétaire sélectionné
  const ownerHotels = hotels.filter(h => h.ownerId?.toString() === selectedOwner);

  // Charge le relevé live
  const fetchStatement = useCallback(() => {
    if (!selectedOwner || !period.from || !period.to) return;
    setLoadingStatement(true);
    setError(null);
    axios.get(`${API}/statement`, {
      params: { ownerId: selectedOwner, from: period.from, to: period.to },
      withCredentials: true
    })
      .then(r => setStatement(r.data))
      .catch(() => setError("Impossible de charger le relevé."))
      .finally(() => setLoadingStatement(false));
  }, [selectedOwner, period.from, period.to]);

  useEffect(() => {
    if (selectedOwner) fetchStatement();
    else setStatement(null);
  }, [selectedOwner, period.from, period.to, fetchStatement]);

  const handleAddExpense = async (data) => {
    setExpenseLoading(true);
    try {
      await axios.post(`${API}/expenses`, data, { withCredentials: true });
      setShowExpenseForm(false);
      fetchStatement(); // recharge le relevé avec la nouvelle dépense
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'ajout.");
    } finally {
      setExpenseLoading(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    try {
      await axios.delete(`${API}/expenses/${id}`, { withCredentials: true });
      fetchStatement();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la suppression.");
    }
  };

  const handleMarkPaid = async () => {
    if (!statement || statement.summary.reservationCount === 0) return;
    if (!window.confirm(`Confirmer le reversement de ${eur(statement.summary.netAmount)} à ${statement.owner.name} ?`)) return;
    setPaying(true);
    try {
      await axios.post(`${API}/statement/pay`, {
        ownerId: selectedOwner, from: period.from, to: period.to
      }, { withCredentials: true });
      // Recharge historique + relevé (qui sera vide maintenant)
      const p = await axios.get(`${API}/payouts`, { withCredentials: true });
      setPayouts(p.data);
      fetchStatement();
      alert("Reversement enregistré.");
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setPaying(false);
    }
  };

  const s = statement?.summary;

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Reversements</h1>
          <p className="text-sm text-gray-400 mt-0.5">Relevés propriétaires et historique des virements</p>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="flex items-center gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: "statement", label: "Nouveau relevé", icon: TbFileInvoice },
          { key: "history",   label: "Historique",     icon: TbHistory },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
                tab === t.key ? "bg-[var(--k-green)] text-white" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
          <TbAlertCircle className="text-red-500 shrink-0" size={18} />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* ═══════════ TAB RELEVÉ ═══════════ */}
      {tab === "statement" && (
        <>
          {/* Sélecteurs */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1.5 flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-500">Propriétaire</label>
              <select
                value={selectedOwner}
                onChange={e => setSelectedOwner(e.target.value)}
                className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
              >
                <option value="">— Sélectionner un propriétaire —</option>
                {owners.map(o => (
                  <option key={o._id} value={o._id}>{o.name} ({o.commissionRate}%)</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">Du</label>
              <input
                type="date" value={period.from}
                onChange={e => setPeriod(p => ({ ...p, from: e.target.value }))}
                className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-500">Au</label>
              <input
                type="date" value={period.to}
                onChange={e => setPeriod(p => ({ ...p, to: e.target.value }))}
                className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white outline-none focus:border-[var(--k-green)] transition"
              />
            </div>
            <button
              onClick={fetchStatement}
              className="w-10 h-10 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
            >
              <TbRefresh size={16} />
            </button>
          </div>

          {/* Pas de proprio sélectionné */}
          {!selectedOwner && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
                <TbFileInvoice className="text-gray-400" size={24} />
              </div>
              <p className="text-sm text-gray-400">Sélectionnez un propriétaire pour voir son relevé</p>
            </div>
          )}

          {/* Relevé */}
          {selectedOwner && loadingStatement && (
            <div className="flex items-center justify-center py-20">
              <p className="text-sm text-gray-400">Calcul du relevé...</p>
            </div>
          )}

          {selectedOwner && !loadingStatement && statement && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

              {/* ── COLONNE GAUCHE — Détails ── */}
              <div className="lg:col-span-2 flex flex-col gap-4">

                {/* Réservations */}
                <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                      <TbReceipt className="text-blue-600" size={14} />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      Réservations terminées ({s.reservationCount})
                    </p>
                  </div>
                  {statement.reservations.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-6">Aucune réservation terminée sur cette période</p>
                  ) : (
                    <div className="flex flex-col">
                      {statement.reservations.map(r => (
                        <div key={r._id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {r.guest?.firstName} {r.guest?.lastName}
                            </p>
                            <p className="text-xs text-gray-400 flex items-center gap-1.5">
                              <TbBuilding size={11} /> {r.hotelId?.name || "—"}
                              <span className="text-gray-300">·</span>
                              <TbDoorEnter size={11} className="text-emerald-500" /> {fmtShort(r.checkInDate)}
                              <TbDoorExit size={11} className="text-blue-500" /> {fmtShort(r.checkOutDate)}
                            </p>
                          </div>
                          <p className="text-sm font-bold text-gray-900 shrink-0">{eur(r.totalPrice)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dépenses */}
                <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
                        <TbCurrencyEuro className="text-red-500" size={14} />
                      </div>
                      <p className="text-sm font-semibold text-gray-900">
                        Dépenses ({s.expenseCount})
                      </p>
                    </div>
                    <button
                      onClick={() => setShowExpenseForm(v => !v)}
                      className="flex items-center gap-1 text-xs text-[var(--k-green)] hover:underline"
                    >
                      {showExpenseForm ? <TbX size={13} /> : <TbPlus size={13} />}
                      {showExpenseForm ? "Annuler" : "Ajouter"}
                    </button>
                  </div>

                  {showExpenseForm && (
                    <div className="mb-3">
                      <ExpenseForm
                        ownerId={selectedOwner}
                        hotels={ownerHotels}
                        onSave={handleAddExpense}
                        onCancel={() => setShowExpenseForm(false)}
                        loading={expenseLoading}
                      />
                    </div>
                  )}

                  {statement.expenses.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-6">Aucune dépense sur cette période</p>
                  ) : (
                    <div className="flex flex-col">
                      {statement.expenses.map(ex => (
                        <div key={ex._id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0 group">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{ex.label}</p>
                            <p className="text-xs text-gray-400">
                              {fmtShort(ex.date)}{ex.hotelId?.name ? ` · ${ex.hotelId.name}` : ""}
                            </p>
                          </div>
                          <p className="text-sm font-bold text-red-500 shrink-0">− {eur(ex.amount)}</p>
                          <button
                            onClick={() => handleDeleteExpense(ex._id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                          >
                            <TbTrash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── COLONNE DROITE — Calcul + action ── */}
              <div className="flex flex-col gap-4">
                <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm sticky top-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                      <TbFileInvoice className="text-[var(--k-green)]" size={14} />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">Relevé à reverser</p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 mb-4">
                    <CalcRow label="Revenus bruts (voyageurs)" value={s.grossRevenue} />
                    {s.komorotelFee > 0 && <CalcRow label="Commission Komorotel (8%)" value={s.komorotelFee} color="text-blue-500" negative />}
                    <CalcRow label={`Commission agence (${statement.owner.commissionRate}%)`} value={s.agencyCommission} color="text-orange-500" negative />
                    {s.totalExpenses > 0 && <CalcRow label="Dépenses déduites" value={s.totalExpenses} color="text-red-400" negative />}
                    <div className="flex items-center justify-between pt-2.5 mt-1 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-700">Net à reverser</p>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[var(--k-green)]">{eur(s.netAmount)}</p>
                        <p className="text-xs text-gray-400">{toKMF(s.netAmount)} KMF</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleMarkPaid}
                    disabled={paying || s.reservationCount === 0}
                    className="w-full py-3 bg-[var(--k-green)] text-white text-sm font-medium rounded-2xl hover:bg-[var(--k-green-hover)] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <TbCheck size={16} />
                    {paying ? "Enregistrement..." : "Marquer comme payé"}
                  </button>
                  {s.reservationCount === 0 && (
                    <p className="text-xs text-gray-400 text-center mt-2">
                      Aucune réservation à reverser sur cette période
                    </p>
                  )}
                  <p className="text-[11px] text-gray-400 text-center mt-3">
                    Une fois marqué payé, ce relevé est archivé et les dépenses liées ne seront plus recomptées.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════ TAB HISTORIQUE ═══════════ */}
      {tab === "history" && (
        <>
          {payouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
                <TbHistory className="text-gray-400" size={24} />
              </div>
              <p className="text-sm text-gray-400">Aucun reversement effectué pour l'instant</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {payouts.map(p => <PayoutHistoryCard key={p._id} payout={p} />)}
              <p className="text-xs text-gray-400 text-center mt-2">
                {payouts.length} reversement{payouts.length > 1 ? "s" : ""} · Total versé : {eur(payouts.reduce((sum, p) => sum + (p.netAmount || 0), 0))}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}